import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {ServicesService} from '../users/services.service';
import {ConfigServices} from '../core/config';
import {DhaPerson, DhaResponse} from './dha-response';

@Injectable({
  providedIn: 'root'
})
export class DhaService extends ServicesService {

  // Values DHA sends when a field is not populated.
  private static readonly BLANK_VALUES = ['', '0', '0000000000000'];
  private static readonly CHILD_COUNT = 21;

  constructor(public http: HttpClient, public configService: ConfigServices) {
    super('dha', http, configService);
  }

  public getDbDha(idNumber: string): Observable<Array<DhaResponse>> {
    const url = this.configService.getApi() + this.type + '/db-dha/' + idNumber;
    return this.http.get<Array<DhaResponse>>(url, {withCredentials: true});
  }

  // An ID number can have more than one stored response, the most recent one is the one that counts.
  public toPerson(dhaResponses: Array<DhaResponse>): DhaPerson {
    if (!dhaResponses || dhaResponses.length === 0) {
      return null;
    }
    const latest = dhaResponses.reduce((mostRecent: DhaResponse, dhaResponse: DhaResponse) =>
      new Date(dhaResponse.created) > new Date(mostRecent.created) ? dhaResponse : mostRecent);
    return this.parsePayload(latest.payload);
  }

  public parsePayload(payload: string): DhaPerson {
    if (!payload) {
      return null;
    }
    const document = new DOMParser().parseFromString(payload.trim(), 'text/xml');
    if (document.getElementsByTagName('parsererror').length > 0) {
      return null;
    }

    const person = new DhaPerson();
    person.error = this.read(document, 'ERROR');
    person.idNumber = this.read(document, 'IDNUMBER');
    person.newIdNumber = this.read(document, 'NEWIDNO');
    person.surname = this.read(document, 'SURNAME');
    person.names = this.read(document, 'NAMES');
    person.maidenName = this.read(document, 'MAIDEN_NAME');
    person.deathStatus = this.read(document, 'DEATH_STATUS');
    person.birthPlace = this.read(document, 'BIRTH_PLACE');
    person.dateOfBirth = this.readDate(document, 'DOB');
    person.dateOfDeath = this.readDate(document, 'DOD');
    person.deathPlace = this.read(document, 'DEATH_PLACE');
    person.marriageStatus = this.read(document, 'MARRIAGE_STATUS');
    person.marriageDate = this.readDate(document, 'MARRIAGE_DATE');
    person.marriagePlace = this.read(document, 'MARRIAGE_PLACE');
    person.spouseIdNumber = this.read(document, 'SPOUSE_ID');
    person.motherIdNumber = this.read(document, 'MOTHER_ID');
    person.fatherIdNumber = this.read(document, 'FATHER_ID');

    for (let a = 1; a <= DhaService.CHILD_COUNT; a++) {
      const child = this.read(document, 'CHILD_' + (a < 10 ? '0' + a : a) + '_ID');
      if (child) {
        person.children.push(child);
      }
    }
    return person;
  }

  // Blank, '0' and '0000000000000' all mean 'nothing on record', so read them back as empty.
  private read(document: Document, tagName: string): string {
    const elements = document.getElementsByTagName(tagName);
    if (elements.length === 0 || !elements[0].textContent) {
      return '';
    }
    const value = elements[0].textContent.trim();
    return DhaService.BLANK_VALUES.indexOf(value) > -1 ? '' : value;
  }

  // Dates arrive as yyyyMMdd.
  private readDate(document: Document, tagName: string): string {
    const value = this.read(document, tagName);
    if (value.length !== 8) {
      return value;
    }
    return value.substring(6, 8) + '/' + value.substring(4, 6) + '/' + value.substring(0, 4);
  }
}
